import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  Clock, CheckCircle2, XCircle, AlertTriangle, SkipForward,
  RefreshCw, Calendar, ChevronRight, Droplets, Flame, UtensilsCrossed, Sparkles,
} from "lucide-react";

const MX_TZ = "America/Mexico_City";

function toMxTime(isoStr) {
  if (!isoStr) return "--:--";
  try {
    return new Date(isoStr).toLocaleTimeString("es-MX", {
      timeZone: MX_TZ,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "--:--";
  }
}

function mxHour(isoStr) {
  if (!isoStr) return -1;
  try {
    return parseInt(new Date(isoStr).toLocaleString("es-MX", { timeZone: MX_TZ, hour: "numeric", hour12: false }), 10);
  } catch {
    return -1;
  }
}

const STATUS_LABEL = {
  pendiente: "Pendiente",
  en_progreso: "En progreso",
  completado: "Completado",
  atrasado: "Atrasado",
  omitido: "Omitido",
};

const STATUS_COLOR = {
  pendiente: "border-slate-300 bg-white text-slate-700",
  en_progreso: "border-yellow-400 bg-yellow-50 text-yellow-800",
  completado: "border-green-400 bg-green-50 text-green-800",
  atrasado: "border-red-400 bg-red-50 text-red-800",
  omitido: "border-slate-200 bg-slate-50 text-slate-400 line-through",
};

const STATUS_DOT = {
  pendiente: "bg-slate-400",
  en_progreso: "bg-yellow-500",
  completado: "bg-green-500",
  atrasado: "bg-red-500",
  omitido: "bg-slate-300",
};

const CAT_ICON = {
  limpieza_banos: Droplets,
  limpieza_cocina: Flame,
  limpieza_comedor: UtensilsCrossed,
  limpieza_general: Sparkles,
};

const CAT_LABEL = {
  limpieza_banos: "Banos",
  limpieza_cocina: "Cocina",
  limpieza_comedor: "Comedor",
  limpieza_general: "General",
};

function ExecutionCard({ ex, onAction, user, compact = false }) {
  const navigate = useNavigate();
  const role = user?.role;
  const uid = user?.id;
  const userPos = user?.position_code;

  const canClaim =
    ex.status === "pendiente" &&
    !ex.assigned_to_user_id &&
    (role !== "operativo" || !userPos || userPos === ex.responsible_position);

  const canStart =
    (ex.status === "pendiente" || ex.status === "atrasado") &&
    (role !== "operativo" || ex.assigned_to_user_id === uid);

  const canComplete =
    ["en_progreso", "pendiente", "atrasado"].includes(ex.status) &&
    (role !== "operativo" || !ex.assigned_to_user_id || ex.assigned_to_user_id === uid);

  const canSkip =
    !["completado", "omitido"].includes(ex.status) &&
    ["corporate_admin", "branch_manager"].includes(role);

  const CatIcon = CAT_ICON[ex.category] || Sparkles;

  return (
    <div
      data-testid={`execution-card-${ex.id}`}
      className={`rounded-lg border p-2 ${compact ? "text-xs" : "text-sm"} ${STATUS_COLOR[ex.status]} transition-all`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-0.5">
            <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[ex.status]}`} />
            <CatIcon className="w-3 h-3 flex-shrink-0" />
            <span className={`font-medium truncate ${compact ? "max-w-[100px]" : "max-w-[200px]"}`}>{ex.title}</span>
          </div>
          <div className="text-xs opacity-70 pl-5">
            {toMxTime(ex.scheduled_at)} · {ex.responsible_position}
            {ex.assigned_to_user_id && !compact && (
              <span className="ml-1 text-green-700">(asignado)</span>
            )}
          </div>
        </div>
        <button
          data-testid={`ex-detail-btn-${ex.id}`}
          onClick={() => navigate(`/ops/limpieza/${ex.id}`)}
          className="p-0.5 rounded hover:bg-black/5 flex-shrink-0"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {!compact && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {canClaim && (
            <button
              data-testid={`claim-btn-${ex.id}`}
              onClick={() => onAction("claim", ex)}
              className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700"
            >
              Tomar
            </button>
          )}
          {canStart && !["completado", "omitido"].includes(ex.status) && (
            <button
              data-testid={`start-btn-${ex.id}`}
              onClick={() => onAction("start", ex)}
              className="px-2 py-0.5 bg-yellow-500 text-white rounded text-xs font-medium hover:bg-yellow-600"
            >
              Iniciar
            </button>
          )}
          {canComplete && (
            <button
              data-testid={`complete-btn-${ex.id}`}
              onClick={() => navigate(`/ops/limpieza/${ex.id}`)}
              className="px-2 py-0.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700"
            >
              Completar
            </button>
          )}
          {canSkip && (
            <button
              data-testid={`skip-btn-${ex.id}`}
              onClick={() => onAction("skip", ex)}
              className="px-2 py-0.5 bg-slate-400 text-white rounded text-xs font-medium hover:bg-slate-500"
            >
              Omitir
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TimelineDesktop({ executions, onAction, user }) {
  const hours = Array.from({ length: 15 }, (_, i) => i + 8); // 08:00 – 22:00
  const byHour = {};
  executions.forEach(ex => {
    const h = mxHour(ex.scheduled_at);
    if (h >= 8 && h <= 22) {
      if (!byHour[h]) byHour[h] = [];
      byHour[h].push(ex);
    }
  });

  return (
    <div className="overflow-x-auto pb-4" data-testid="timeline-desktop">
      <div className="flex min-w-max border border-slate-200 rounded-xl overflow-hidden">
        {hours.map(h => (
          <div key={h} className="flex-none w-44 border-r border-slate-200 last:border-r-0">
            <div className="h-9 bg-slate-50 border-b border-slate-200 flex items-center justify-center">
              <span className="text-xs font-semibold text-slate-500">{String(h).padStart(2, "0")}:00</span>
            </div>
            <div className="p-1.5 space-y-1.5 min-h-28">
              {(byHour[h] || []).map(ex => (
                <ExecutionCard key={ex.id} ex={ex} onAction={onAction} user={user} compact />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileList({ executions, onAction, user }) {
  const sorted = [...executions].sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  return (
    <div className="space-y-2" data-testid="mobile-list">
      {sorted.map(ex => (
        <ExecutionCard key={ex.id} ex={ex} onAction={onAction} user={user} />
      ))}
    </div>
  );
}

export default function OpsLimpieza() {
  const { user } = useAuth();
  const [date, setDate] = useState(() =>
    new Date().toLocaleDateString("fr-CA", { timeZone: "America/Mexico_City" })
  );
  const [branchFilter, setBranchFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [executions, setExecutions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("timeline"); // "timeline" | "list"
  const [skipModal, setSkipModal] = useState(null); // { ex }
  const [skipReason, setSkipReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const isAdmin = ["corporate_admin"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date });
      if (branchFilter) params.set("branch_id", branchFilter);
      if (statusFilter) params.set("status", statusFilter);
      const res = await api.get(`/ops/cleaning/executions?${params}`);
      let data = res.data;
      if (categoryFilter) data = data.filter(e => e.category === categoryFilter);
      setExecutions(data);
    } catch {
      setExecutions([]);
    } finally {
      setLoading(false);
    }
  }, [date, branchFilter, categoryFilter, statusFilter]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/branches").then(r => setBranches(r.data.filter(b => !b.is_corporate))).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (action, ex) => {
    if (action === "skip") {
      setSkipModal({ ex });
      setSkipReason("");
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/ops/cleaning/executions/${ex.id}/${action}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al ejecutar la acción");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSkipConfirm = async () => {
    if (!skipReason.trim()) return;
    setActionLoading(true);
    try {
      await api.post(`/ops/cleaning/executions/${skipModal.ex.id}/skip`, { reason: skipReason });
      setSkipModal(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al omitir la tarea");
    } finally {
      setActionLoading(false);
    }
  };

  const atrasados = executions.filter(e => e.status === "atrasado").length;
  const completados = executions.filter(e => e.status === "completado").length;
  const total = executions.length;

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Limpieza</h1>
          <p className="text-sm text-slate-500">Calendario de tareas del dia</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="refresh-btn"
            onClick={load}
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
          >
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
          <div className="hidden md:flex border border-slate-200 rounded-lg overflow-hidden">
            <button
              data-testid="view-timeline-btn"
              onClick={() => setViewMode("timeline")}
              className={`px-3 py-1.5 text-sm ${viewMode === "timeline" ? "bg-[#FFDF00] font-semibold" : "hover:bg-slate-50"}`}
            >
              Timeline
            </button>
            <button
              data-testid="view-list-btn"
              onClick={() => setViewMode("list")}
              className={`px-3 py-1.5 text-sm ${viewMode === "list" ? "bg-[#FFDF00] font-semibold" : "hover:bg-slate-50"}`}
            >
              Lista
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          data-testid="date-filter"
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
        />
        {isAdmin && (
          <select
            value={branchFilter}
            onChange={e => setBranchFilter(e.target.value)}
            data-testid="branch-filter"
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
          >
            <option value="">Todas las sucursales</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          data-testid="category-filter"
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
        >
          <option value="">Todas las categorias</option>
          <option value="limpieza_banos">Banos</option>
          <option value="limpieza_cocina">Cocina</option>
          <option value="limpieza_comedor">Comedor</option>
          <option value="limpieza_general">General</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          data-testid="status-filter"
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="en_progreso">En progreso</option>
          <option value="completado">Completado</option>
          <option value="atrasado">Atrasado</option>
          <option value="omitido">Omitido</option>
        </select>
      </div>

      {/* Banner atrasados */}
      {atrasados > 0 && (
        <div
          data-testid="atrasos-banner"
          className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2"
        >
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 font-medium">
            {atrasados} tarea{atrasados !== 1 ? "s" : ""} atrasada{atrasados !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Stats rápidas */}
      {total > 0 && (
        <div className="flex gap-3 flex-wrap">
          <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1 font-medium">
            {total} tareas
          </span>
          <span className="text-xs bg-green-100 text-green-700 rounded-full px-3 py-1 font-medium">
            {completados} completadas
          </span>
          {total > 0 && (
            <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1 font-medium">
              {Math.round((completados / total) * 100)}% cumplimiento
            </span>
          )}
        </div>
      )}

      {/* Contenido */}
      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="loading-state">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : executions.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="empty-state">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay tareas para esta fecha o filtro.</p>
          {isAdmin && (
            <button
              data-testid="generate-today-btn"
              onClick={async () => {
                try {
                  await api.post("/ops/cleaning/executions/generate-today");
                  await load();
                } catch (e) {
                  alert(e.response?.data?.detail || "Error al generar tareas");
                }
              }}
              className="mt-3 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-400"
            >
              Generar tareas para hoy
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="md:hidden">
            <MobileList executions={executions} onAction={handleAction} user={user} />
          </div>
          <div className="hidden md:block">
            {viewMode === "timeline" ? (
              <TimelineDesktop executions={executions} onAction={handleAction} user={user} />
            ) : (
              <MobileList executions={executions} onAction={handleAction} user={user} />
            )}
          </div>
        </>
      )}

      {/* Modal omitir */}
      {skipModal && (
        <div
          data-testid="skip-modal"
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={e => e.target === e.currentTarget && setSkipModal(null)}
        >
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-slate-900 mb-1">Omitir tarea</h3>
            <p className="text-sm text-slate-500 mb-4">{skipModal.ex.title}</p>
            <textarea
              data-testid="skip-reason-input"
              value={skipReason}
              onChange={e => setSkipReason(e.target.value)}
              placeholder="Motivo de omision (obligatorio)"
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] mb-4"
            />
            <div className="flex gap-2">
              <button
                data-testid="skip-cancel-btn"
                onClick={() => setSkipModal(null)}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                data-testid="skip-confirm-btn"
                onClick={handleSkipConfirm}
                disabled={!skipReason.trim() || actionLoading}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
              >
                {actionLoading ? "..." : "Confirmar omision"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
